import PictureImage from '../Common/PictureImage';
import React, { useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { IMAGE_ALT } from '../../seo/imageAltText';
import './WhyPartner.css';

const WhyPartner: React.FC = () => {
  // Mobile slider ref for auto-scroll
  const cardsSliderRef = useRef<HTMLDivElement>(null);
  const autoScrollIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const cards: { id: number; title: string; description: React.ReactNode; image: string }[] = [
    {
      id: 1,
      title: 'Advanced Technology Behind Our Seller Fulfilled Prime UK Services',
      description: (
        <>
          Our SFP operation uses advanced warehouse technology and automated fulfilment — covered in depth in{' '}
          <Link to="/blog/seller-fulfilled-prime-fulfilment-uk">Seller Fulfilled Prime fulfilment UK explained</Link>.
        </>
      ),
      image: '/sfp/Backed by cutting-edge tech.jpg'
    },
    {
      id: 2,
      title: '24-Hour Stock Check-In',
      description: 'Receive and book inventory into our warehouse within 24 hours, helping you launch products faster and keep Seller Fulfilled Prime orders moving without unnecessary delays.',
      image: '/Images/59887.webp'
    },
    {
      id: 3,
      title: 'Three-Stage Quality Control',
      description: (
        <>
          Every shipment is inspected before SFP dispatch; damaged stock can route into{' '}
          <Link to="/return-handling-services">return handling</Link> for grading and recovery.
        </>
      ),
      image: '/sfp/Quality control the Pick Pack Pro way.jpg'
    },
    {
      id: 4,  
      title: 'Secure Warehouse Storage',
      description: (
        <>
          Products stay in secure UK facilities at our{' '}
          <Link to="/fulfilment-centre-milton-keynes">Milton Keynes fulfilment centre</Link> until dispatch.
        </>
      ),
      image: '/sfp/Safe and secure storage.jpg'
    },
    {
      id: 5,
      title: 'Smart Inventory Management',
      description: 'Real-time inventory tracking helps forecast demand, reduce stock shortages, and maintain accurate stock levels for Seller Fulfilled Prime UK orders.',
      image: '/sfp/Accurate demand forecasting.jpg'
    },
    {
      id: 6,
      title: 'Reliable UK Shipping',
      description: 'We partner with trusted courier networks to provide fast, dependable deliveries that help Amazon sellers achieve Seller Fulfilled Prime performance targets and exceed customer expectations.',
      image: '/sfp/The best shipping services.jpg'
    }
  ];

  // Auto-scroll functionality for mobile
  useEffect(() => {
    const slider = cardsSliderRef.current;
    if (!slider) return;

    const isMobile = window.innerWidth <= 768;
    if (!isMobile) return;

    let currentIndex = 0;
    const getCardWidth = () => {
      const containerWidth = slider.offsetWidth;
      return containerWidth * 0.85; // 85% width for each card
    };
    const gap = 16;

    const scrollToCard = (index: number) => {
      if (!slider) return;
      const cardWidth = getCardWidth();
      const scrollPosition = index * (cardWidth + gap);
      slider.scrollTo({
        left: scrollPosition,
        behavior: 'smooth'
      });
    };

    const autoScroll = () => {
      currentIndex = (currentIndex + 1) % cards.length;
      scrollToCard(currentIndex);
    };

    // Start auto-scroll after initial delay
    const startAutoScroll = setTimeout(() => {
      autoScrollIntervalRef.current = setInterval(autoScroll, 4000); // Scroll every 4 seconds
    }, 2000);

    // Cleanup
    return () => {
      clearTimeout(startAutoScroll);
      if (autoScrollIntervalRef.current) {
        clearInterval(autoScrollIntervalRef.current);
      }
    };
  }, []);

  return (
    <section className="why-partner-section">
      <div className="why-partner-container">
        {/* Top Section: Left Text, Right Image */}
        <div className="why-partner-top">
          <div className="why-partner-top-content">
            {/* Left Side - Text */}
            <div className="why-partner-text">
              <h2 className="why-partner-title">
                Why Partner with Pick Pack Pro for Seller Fulfilled Prime UK?
              </h2>
              <p className="why-partner-description">
                With years of ecommerce fulfilment experience, Pick Pack Pro helps Amazon sellers succeed with
                Seller Fulfilled Prime UK. Our services combine fast fulfilment and dependable UK delivery —
                start with the{' '}
                <Link to="/blog/what-is-seller-fulfilled-prime-uk-complete-guide-2026">
                  complete Seller Fulfilled Prime UK guide
                </Link>{' '}
                or{' '}
                <Link to="/blog/seller-fulfilled-prime-vs-fba">Seller Fulfilled Prime vs FBA explained</Link>{' '}
                if you are still choosing a model.
              </p>
              
              {/* <div className="why-partner-feature">
                <h3 className="why-partner-feature-title">
                  Ultra-late order cut-off time of{' '}
                  <span className="why-partner-feature-highlight">9pm</span>
                </h3>
                <p className="why-partner-feature-description">
                  When <strong>35% of online orders are placed between 4pm and 9pm</strong>, you can't afford to have 
                  an early order cut-off time. That's why ours is 9pm, giving your customers the chance to order late 
                  into the evening for next-day delivery.*
                </p>
                <p className="why-partner-feature-description">
                  Our industry-leading cut-off time gives you an <strong>additional 25+ hours</strong> a week to achieve 
                  optimal conversion rates during peak sales hours. Ultra-fast and flexible order processing times means 
                  you can offer the best fulfilment experiences to your customers.
                </p>
                <p className="why-partner-footnote">
                  *UK mainland. Monday to Friday. Courier specific.
                </p>
              </div> */}
            </div>
            
            {/* Right Side - Image */}
            <div className="why-partner-image-wrapper">
              <PictureImage 
                src="/sfp/Why Partner With Pick Pack.jpg" 
                alt={IMAGE_ALT.sfpWhyPartnerHero} 
                className="why-partner-image"
              />
            </div>
          </div>
        </div>

        {/* Desktop: First Row: 3 Cards */}
        <div className="why-partner-cards-row">
          {cards.slice(0, 3).map((card) => (
            <div key={card.id} className="why-partner-card">
              {card.image && (
                <div className="why-partner-card-image-wrapper">
                  <PictureImage 
                    src={card.image} 
                    alt={IMAGE_ALT.sfpPartnerCard(card.title)}
                    className="why-partner-card-image"
                  />
                </div>
              )}
              <h3 className="why-partner-card-title">{card.title}</h3>
              <p className="why-partner-card-description">{card.description}</p>
            </div>
          ))}
        </div>

        {/* Desktop: Second Row: 3 Cards */}
        <div className="why-partner-cards-row">
          {cards.slice(3, 6).map((card) => (
            <div key={card.id} className="why-partner-card">
              {card.image && (
                <div className="why-partner-card-image-wrapper">
                  <PictureImage 
                    src={card.image} 
                    alt={card.title}
                    className="why-partner-card-image"
                  />
                </div>
              )}
              <h3 className="why-partner-card-title">{card.title}</h3>
              <p className="why-partner-card-description">{card.description}</p>
            </div>
          ))}
        </div>

        {/* Mobile Slider: All Cards */}
        <div className="why-partner-mobile-slider" ref={cardsSliderRef}>
          {cards.map((card) => (
            <div key={card.id} className="why-partner-mobile-slide">
              <div className="why-partner-mobile-card">
                {card.image && (
                  <div className="why-partner-card-image-wrapper">
                    <PictureImage 
                      src={card.image} 
                      alt={card.title}
                      className="why-partner-card-image"
                    />
                  </div>
                )}
                <p className="why-partner-card-title">{card.title}</p>
                <p className="why-partner-card-description">{card.description}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default WhyPartner;
